// WHAT: Modal component for managing roadmap modules (create, delete)
// WHY:  Provides interface for administrators to manage available roadmap modules
// DOMAIN: Admin → Roadmap
// DEPENDENCIES: Tauri core API, Lucide icons
// RISKS:
//   - WARN: Error handling displays errors but doesn't prevent state updates
//   - NOTE: 'core' module is protected from deletion but validation is client-side only
//   - NOTE: No confirmation dialog before deleting modules

import { core } from "@tauri-apps/api";
import { PlusCircle, Trash2, Settings } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Props for ModuleManager component
 */
interface Properties {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
}

/**
 * WHAT:
 *   Modal component for managing roadmap modules - creating new modules and
 *   deleting existing ones (except 'core' module).
 *
 * WHY:
 *   Provides administrators with a way to manage the available modules that
 *   can be assigned to roadmap items.
 *
 * PROPS:
 *   - isOpen: boolean — Controls modal visibility
 *   - onClose: () => void — Handler to close the modal
 *
 * BEHAVIOR:
 *   - Loads modules when modal opens
 *   - Allows creating new modules (with duplicate check)
 *   - Allows deleting modules (except 'core')
 *   - Shows loading and error states
 *   - Validates module name length (minimum 2 characters)
 *
 * RISKS:
 *   - WARN: 'core' module protection is client-side only - backend should enforce
 *   - NOTE: No confirmation dialog before deletion - accidental deletions possible
 *   - NOTE: Error state is set but user may not notice if error message is off-screen
 *   - NOTE: Duplicate check is case-sensitive - "Module" and "module" are different
 *
 * @returns {JSX.Element} Die gerenderte ModuleManager-Komponente
 *
 * @param root0
 *
 * @param root0.isOpen
 *
 * @param root0.onClose
 *
 */
export const ModuleManager = ({ isOpen, onClose }: Properties) => {
  // NOTE: State for list of available modules
  const [modules, setModules] = useState<string[]>([]);
  // NOTE: Loading state during module fetch
  const [loading, setLoading] = useState(true);
  // NOTE: Error state for displaying error messages
  const [error, setError] = useState<null | string>(null);
  // NOTE: Form state for new module name
  const [newModule, setNewModule] = useState("");

  // NOTE: Load modules when modal opens
  useEffect(() => {
    if (isOpen) {
      void loadModules();
    }
  }, [isOpen]);

  /**
   * WHAT:
   *   Fetches all roadmap modules from the backend.
   *
   * WHY:
   *   Loads current module list when modal opens for display and management.
   *
   * BEHAVIOR:
   *   - Sets loading state during fetch
   *   - Updates modules state with fetched list
   *   - Handles errors and displays error message
   *
   * RISKS:
   *   - NOTE: Error is converted to string - may lose error details
   *   - NOTE: No retry mechanism on failure
   */
  const loadModules = async () => {
    setLoading(true);
    try {
      const list = await core.invoke<string[]>("fetch_roadmap_modules");
      setModules(list);
      setError(null);
    } catch (error_) {
      // NOTE: Error handling - display error to user
      setError(String(error_));
    } finally {
      setLoading(false);
    }
  };

  /**
   * WHAT:
   *   Creates a new roadmap module in the backend and updates local state.
   *
   * WHY:
   *   Allows administrators to add new modules that can be assigned to roadmap items.
   *
   * BEHAVIOR:
   *   - Checks for duplicate module names (case-sensitive)
   *   - Inserts module into database via Tauri command
   *   - Updates local state with new module
   *   - Clears form field after successful creation
   *
   * RISKS:
   *   - NOTE: Duplicate check is case-sensitive - "Module" and "module" are different
   *   - NOTE: No validation beyond duplicate check and minimum length
   *   - NOTE: Error handling displays error but doesn't prevent state update
   *
   * @param name
   *
   */
  const addModule = async (name: string) => {
    if (!modules.includes(name)) {
      try {
        await core.invoke("insert_roadmap_module", { name });
        setModules([...modules, name]);
        setNewModule("");
      } catch (error_) {
        // NOTE: Error handling - display error to user
        setError(String(error_));
      }
    }
  };

  /**
   * WHAT:
   *   Deletes a roadmap module from the backend and updates local state.
   *
   * WHY:
   *   Allows administrators to remove modules that are no longer needed.
   *
   * BEHAVIOR:
   *   - Deletes module via Tauri command
   *   - Removes module from local state
   *   - 'core' module is protected (not shown in delete button)
   *
   * RISKS:
   *   - WARN: No confirmation dialog - accidental deletions possible
   *   - NOTE: 'core' protection is UI-only - backend should also enforce
   *   - NOTE: No check for roadmap items using this module before deletion
   *   - NOTE: Error handling displays error but doesn't prevent state update
   *
   * @param name
   *
   */
  const deleteModule = async (name: string) => {
    try {
      await core.invoke("delete_roadmap_module", { name });
      setModules(modules.filter((module_) => module_ !== name));
    } catch (error_) {
      // NOTE: Error handling - display error to user
      setError(String(error_));
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-[#212B36] rounded-2xl shadow-lg p-6 max-w-md w-full text-[#F1F5F9] space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Module Manager
          </h2>
          <button className="text-[#CBD5E1] hover:text-white transition-colors" onClick={onClose}>
            ✕
          </button>
        </div>

        {error ? (
          <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3 text-red-300 text-sm">
            {error}
          </div>
        ) : null}

        {/* Add New Module */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-[#CBD5E1]">Neues Modul erstellen</h3>
          <div className="flex gap-2">
            <input
              className="input-field flex-grow"
              onChange={(e) => {
                setNewModule(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newModule.trim().length > 1) {
                  void addModule(newModule.trim());
                }
              }}
              placeholder="Modulname eingeben..."
              type="text"
              value={newModule}
            />
            <button
              className="flex items-center gap-1 px-3 py-2 bg-tracq-bright-blue hover:bg-tracq-deep-blue disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all duration-200"
              disabled={newModule.trim().length <= 1}
              onClick={() => {
                if (newModule.trim().length > 1) {
                  void addModule(newModule.trim());
                }
              }}
              type="button"
            >
              <PlusCircle className="w-4 h-4" />
              Add
            </button>
          </div>
        </div>

        {/* Module List */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-[#CBD5E1]">
            Verfügbare Module ({modules.length})
          </h3>

          {(() => {
            if (loading) {
              return <div className="text-center py-4 text-[#CBD5E1]">Lade Module...</div>;
            }
            if (modules.length === 0) {
              return <div className="text-center py-4 text-[#CBD5E1]">Keine Module vorhanden</div>;
            }
            return (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {modules.map((module_) => (
                  <div
                    className="flex justify-between items-center px-3 py-2 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
                    key={module_}
                  >
                    <span className="text-[#F1F5F9] font-medium">{module_}</span>
                    {module_ !== "core" && (
                      <button
                        className="text-red-500 hover:text-red-300 transition-colors p-1"
                        onClick={() => {
                          void deleteModule(module_);
                        }}
                        title={`Modul "${module_}" löschen`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-slate-700">
          <button
            className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg font-medium transition-all duration-200"
            onClick={onClose}
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};
